import { DateFormatter } from '@repo/toolkit/date'
import { addDays, isAfter, isBefore, parseISO } from 'date-fns'
import { z, ZodType } from 'zod'

import { OtoIndividualTripStepsFormValues } from '../../types/models/enrollment/oto-individual-trip-steps'
import { NullableTransformer, Time } from '../../types/utils'
import { createDateStringSchema, createTimeSchema } from './date.validation'
import { createStringSchema } from './string-validation'

// the backend refuses the same things with the same wording — it stays the source of truth,
// the form only spares the parents a round trip
const DEPARTURE_AFTER_ARRIVAL_MESSAGE =
  "L'heure de départ doit être antérieure à l'heure d'arrivée"
const RETURN_BEFORE_DEPARTURE_MESSAGE = "Le retour doit avoir lieu après l'aller"

// What the chronology rules read of a leg — a draft may carry any subset of it
type LegChronologyInput = {
  departureDate?: string | null
  departureTime?: Time | null
  arrivesNextDay?: boolean | null
  arrivalTime?: Time | null
}

// The day the leg lands: its departure day, pushed by one with "Arrivée le lendemain (J+1)"
const landingDay = (leg: LegChronologyInput): Date | undefined =>
  leg.departureDate ? addDays(parseISO(leg.departureDate), leg.arrivesNextDay ? 1 : 0) : undefined

// Inside a leg the departure has to come before the arrival
const isLegChronological = (leg: LegChronologyInput): boolean => {
  if (!leg.departureTime || !leg.arrivalTime) return true
  const day = parseISO(leg.departureDate ?? '1970-01-01')
  return isBefore(
    DateFormatter.setTime(day, leg.departureTime),
    DateFormatter.setTime(addDays(day, leg.arrivesNextDay ? 1 : 0), leg.arrivalTime)
  )
}

// The retour must leave after the aller has landed — as datetimes when both times are typed, so
// a same-day turnaround stays possible; only a landing strictly after the retour day is refused
// while a time is still missing
const isReturnAfterDeparture = (
  aller: LegChronologyInput,
  retour: LegChronologyInput
): boolean => {
  const allerLanding = landingDay(aller)
  if (!allerLanding || !retour.departureDate) return true
  const retourDay = parseISO(retour.departureDate)

  if (!aller.arrivalTime || !retour.departureTime) return !isAfter(allerLanding, retourDay)

  return isBefore(
    DateFormatter.setTime(allerLanding, aller.arrivalTime),
    DateFormatter.setTime(retourDay, retour.departureTime)
  )
}

// The rules shared by the draft and the submit: run over whatever is typed, silent on what is not
const refineChronology = (
  values: {
    departureTripStep?: LegChronologyInput | null
    returnTripStep?: LegChronologyInput | null
  },
  ctx: z.RefinementCtx
) => {
  const aller = values.departureTripStep
  const retour = values.returnTripStep

  if (aller && !isLegChronological(aller))
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['departureTripStep', 'arrivalTime'],
      message: DEPARTURE_AFTER_ARRIVAL_MESSAGE
    })

  if (retour && !isLegChronological(retour))
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['returnTripStep', 'arrivalTime'],
      message: DEPARTURE_AFTER_ARRIVAL_MESSAGE
    })

  if (aller && retour && !isReturnAfterDeparture(aller, retour))
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [
        'returnTripStep',
        aller.arrivalTime && retour.departureTime ? 'departureTime' : 'departureDate'
      ],
      message: RETURN_BEFORE_DEPARTURE_MESSAGE
    })
}

// "Valider et envoyer à LEC": every field is required but the n° de vol/train and the compagnie
const legSchema = z.object({
  departureDate: createDateStringSchema({ inputName: 'Date de départ' }),
  departurePlace: createStringSchema('Lieu de départ'),
  departureTime: createTimeSchema({ inputName: 'Heure de départ' }),
  arrivesNextDay: z.boolean().nullish(),
  arrivalPlace: createStringSchema("Lieu d'arrivée"),
  arrivalTime: createTimeSchema({ inputName: "Heure d'arrivée" }),
  transportNumber: createStringSchema('N° de vol/train'),
  company: createStringSchema('Compagnie')
})

export const otoIndividualTripStepsFormSchema: ZodType<
  NullableTransformer<OtoIndividualTripStepsFormValues>
> = z
  .object({ departureTripStep: legSchema, returnTripStep: legSchema })
  .superRefine(refineChronology)

// "Enregistrer et continuer plus tard": nothing is required, but what is typed still has to hold
// together — the same chronology the backend enforces on a draft
const draftLegSchema = z.object({
  departureDate: z.string().nullish(),
  departureTime: createTimeSchema({ inputName: 'Heure de départ' }).nullish(),
  arrivesNextDay: z.boolean().nullish(),
  arrivalTime: createTimeSchema({ inputName: "Heure d'arrivée" }).nullish()
})

export const otoIndividualTripStepsDraftSchema = z
  .object({
    departureTripStep: draftLegSchema.nullish(),
    returnTripStep: draftLegSchema.nullish()
  })
  .superRefine(refineChronology)
